(() => {
  const MAIN_IMAGE_SELECTOR = '.customall-wrapper .customall-product-media .main-carousel .product-mockup > img';
  const MEDIA_SELECTOR = '.customall-wrapper .customall-product-media';
  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const SCALE_STEP = 0.25;
  const DRAG_THRESHOLD = 8;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const imageSource = (image) => image?.currentSrc || image?.src || '';

  class CTCustomallLightbox {
    constructor() {
      this.element = this.createElement();
      this.dialog = this.element.querySelector('[data-ct-customall-dialog]');
      this.viewport = this.element.querySelector('[data-ct-customall-viewport]');
      this.image = this.element.querySelector('[data-ct-customall-image]');
      this.closeButton = this.element.querySelector('[data-ct-customall-close]');
      this.previousButton = this.element.querySelector('[data-ct-customall-previous]');
      this.nextButton = this.element.querySelector('[data-ct-customall-next]');
      this.count = this.element.querySelector('[data-ct-customall-count]');
      this.zoomOutButton = this.element.querySelector('[data-ct-customall-zoom-out]');
      this.zoomInButton = this.element.querySelector('[data-ct-customall-zoom-in]');
      this.resetButton = this.element.querySelector('[data-ct-customall-reset]');
      this.level = this.element.querySelector('[data-ct-customall-level]');

      this.images = [];
      this.index = 0;
      this.sourceImage = null;
      this.returnFocus = null;
      this.previousBodyOverflow = '';
      this.scale = MIN_SCALE;
      this.x = 0;
      this.y = 0;
      this.pointers = new Map();
      this.dragPointerId = null;
      this.dragStart = null;
      this.pinchStartDistance = 0;
      this.pinchStartScale = MIN_SCALE;

      document.body.appendChild(this.element);
      this.bindEvents();
      this.renderZoom();
    }

    createElement() {
      const element = document.createElement('div');
      element.className = 'ct-customall-lightbox';
      element.hidden = true;
      element.setAttribute('aria-hidden', 'true');
      element.innerHTML = `
        <div
          class="ct-customall-lightbox__dialog"
          data-ct-customall-dialog
          role="dialog"
          aria-modal="true"
          aria-label="Product image viewer"
          tabindex="-1"
        >
          <button type="button" class="ct-customall-lightbox__close" data-ct-customall-close aria-label="Close image viewer">
            <span aria-hidden="true">×</span>
          </button>
          <button type="button" class="ct-customall-lightbox__nav ct-customall-lightbox__nav--previous" data-ct-customall-previous aria-label="Previous image">
            <span aria-hidden="true">‹</span>
          </button>
          <div class="ct-customall-lightbox__viewport" data-ct-customall-viewport>
            <img class="ct-customall-lightbox__image" data-ct-customall-image alt="" draggable="true">
          </div>
          <button type="button" class="ct-customall-lightbox__nav ct-customall-lightbox__nav--next" data-ct-customall-next aria-label="Next image">
            <span aria-hidden="true">›</span>
          </button>
          <output class="ct-customall-lightbox__count" data-ct-customall-count aria-live="polite"></output>
          <div class="ct-customall-lightbox__controls" aria-label="Image zoom controls">
            <button type="button" class="ct-customall-lightbox__zoom" data-ct-customall-zoom-out aria-label="Zoom out">−</button>
            <output class="ct-customall-lightbox__level" data-ct-customall-level aria-live="polite">100%</output>
            <button type="button" class="ct-customall-lightbox__zoom" data-ct-customall-zoom-in aria-label="Zoom in">+</button>
            <button type="button" class="ct-customall-lightbox__reset" data-ct-customall-reset aria-label="Reset zoom">Reset</button>
          </div>
        </div>
      `;
      return element;
    }

    bindEvents() {
      this.closeButton.addEventListener('click', () => this.close());
      this.previousButton.addEventListener('click', () => this.navigate(-1));
      this.nextButton.addEventListener('click', () => this.navigate(1));
      this.zoomOutButton.addEventListener('click', () => this.setScale(this.scale - SCALE_STEP));
      this.zoomInButton.addEventListener('click', () => this.setScale(this.scale + SCALE_STEP));
      this.resetButton.addEventListener('click', () => this.resetZoom(true));

      this.element.addEventListener('click', (event) => {
        if (event.target === this.element || event.target === this.dialog) this.close();
      });
      this.element.addEventListener('keydown', (event) => this.onKeyDown(event));
      this.viewport.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });
      this.image.addEventListener('dblclick', (event) => this.onDoubleClick(event));
      this.image.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.image.addEventListener('pointermove', (event) => this.onPointerMove(event));
      this.image.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.image.addEventListener('pointercancel', (event) => this.onPointerUp(event));
    }

    getGalleryImages(sourceImage) {
      const media = sourceImage.closest(MEDIA_SELECTOR);
      if (!media) return [sourceImage];

      const mainImages = [...media.querySelectorAll('.main-carousel .product-mockup > img')].filter((image) =>
        imageSource(image)
      );
      const thumbnails = [...media.querySelectorAll('.embla__slide--thumb img')].filter((image) => imageSource(image));

      if (!thumbnails.length) return mainImages;

      const selectedThumbnailIndex = thumbnails.findIndex((image) => image.closest('.embla__slide--thumb')?.classList.contains('is-selected'));
      const activeIndex = selectedThumbnailIndex >= 0 ? selectedThumbnailIndex : 0;

      return thumbnails.map((thumbnail, index) => (index === activeIndex ? sourceImage : thumbnail));
    }

    open(sourceImage) {
      const images = this.getGalleryImages(sourceImage);
      if (!images.length) return;

      this.images = images;
      this.index = Math.max(0, images.indexOf(sourceImage));
      this.sourceImage = sourceImage;
      this.returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : sourceImage;
      this.previousBodyOverflow = document.body.style.overflow;
      this.element.hidden = false;
      this.element.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ct-customall-lightbox-open');
      document.body.style.overflow = 'hidden';
      this.showCurrentImage();
      this.closeButton.focus({ preventScroll: true });
    }

    close() {
      if (this.element.hidden) return;

      this.resetZoom(false);
      this.element.hidden = true;
      this.element.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ct-customall-lightbox-open');
      document.body.style.overflow = this.previousBodyOverflow;
      this.images = [];
      this.sourceImage = null;

      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
    }

    showCurrentImage() {
      const sourceImage = this.images[this.index];
      const source = imageSource(sourceImage);
      if (!source) return;

      this.sourceImage = sourceImage;
      this.image.src = source;
      this.image.alt = sourceImage.alt || document.querySelector('h1')?.textContent?.trim() || 'Product image';
      this.resetZoom(false);
      this.updateNavigation();
    }

    refreshCurrentImage(changedImage) {
      if (this.element.hidden || changedImage !== this.sourceImage) return;
      const source = imageSource(changedImage);
      if (source && this.image.src !== source) this.image.src = source;
    }

    navigate(direction) {
      if (this.images.length < 2) return;
      this.index = (this.index + direction + this.images.length) % this.images.length;
      this.showCurrentImage();
    }

    updateNavigation() {
      const hasMultiple = this.images.length > 1;
      this.previousButton.disabled = !hasMultiple;
      this.nextButton.disabled = !hasMultiple;
      this.count.hidden = !hasMultiple;
      this.count.value = hasMultiple ? `${this.index + 1} / ${this.images.length}` : '';
      this.count.textContent = this.count.value;
    }

    setScale(nextScale) {
      this.scale = clamp(Math.round(nextScale * 100) / 100, MIN_SCALE, MAX_SCALE);
      if (this.scale === MIN_SCALE) {
        this.x = 0;
        this.y = 0;
      } else {
        this.clampPosition();
      }
      this.renderZoom();
    }

    resetZoom(shouldFocus = false) {
      this.scale = MIN_SCALE;
      this.x = 0;
      this.y = 0;
      this.pointers.clear();
      this.dragPointerId = null;
      this.dragStart = null;
      this.pinchStartDistance = 0;
      this.pinchStartScale = MIN_SCALE;
      this.element.classList.remove('ct-customall-lightbox--zoomed', 'ct-customall-lightbox--dragging');
      this.renderZoom();
      if (shouldFocus) this.resetButton.focus();
    }

    clampPosition() {
      const maxX = (this.image.clientWidth * (this.scale - 1)) / 2;
      const maxY = (this.image.clientHeight * (this.scale - 1)) / 2;
      this.x = clamp(this.x, -maxX, maxX);
      this.y = clamp(this.y, -maxY, maxY);
    }

    renderZoom() {
      this.clampPosition();
      this.image.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`;
      this.element.classList.toggle('ct-customall-lightbox--zoomed', this.scale > MIN_SCALE);
      this.level.value = `${Math.round(this.scale * 100)}%`;
      this.level.textContent = this.level.value;
      this.zoomOutButton.disabled = this.scale <= MIN_SCALE;
      this.zoomInButton.disabled = this.scale >= MAX_SCALE;
      this.resetButton.disabled = this.scale <= MIN_SCALE;
    }

    onWheel(event) {
      event.preventDefault();
      this.setScale(this.scale + (event.deltaY < 0 ? SCALE_STEP : -SCALE_STEP));
    }

    onDoubleClick(event) {
      event.preventDefault();
      this.setScale(this.scale > MIN_SCALE ? MIN_SCALE : 2);
    }

    onPointerDown(event) {
      if (event.button > 0) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.image.setPointerCapture?.(event.pointerId);

      if (this.pointers.size === 2) {
        const points = [...this.pointers.values()];
        this.pinchStartDistance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        this.pinchStartScale = this.scale;
      } else if (this.scale > MIN_SCALE) {
        this.dragPointerId = event.pointerId;
        this.dragStart = { pointerX: event.clientX, pointerY: event.clientY, x: this.x, y: this.y };
        this.element.classList.add('ct-customall-lightbox--dragging');
      }
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 2 && this.pinchStartDistance > 0) {
        const points = [...this.pointers.values()];
        const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        this.setScale(this.pinchStartScale * (distance / this.pinchStartDistance));
        event.preventDefault();
        return;
      }

      if (event.pointerId !== this.dragPointerId || !this.dragStart || this.scale <= MIN_SCALE) return;
      this.x = this.dragStart.x + event.clientX - this.dragStart.pointerX;
      this.y = this.dragStart.y + event.clientY - this.dragStart.pointerY;
      this.renderZoom();
      event.preventDefault();
    }

    onPointerUp(event) {
      this.pointers.delete(event.pointerId);
      this.image.releasePointerCapture?.(event.pointerId);

      if (event.pointerId === this.dragPointerId) {
        this.dragPointerId = null;
        this.dragStart = null;
        this.element.classList.remove('ct-customall-lightbox--dragging');
      }

      if (this.pointers.size < 2) {
        this.pinchStartDistance = 0;
        this.pinchStartScale = this.scale;
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.navigate(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.navigate(1);
      } else if (event.key === 'Tab') {
        const focusable = [...this.element.querySelectorAll('button:not(:disabled)')].filter((button) => !button.hidden);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  }

  let lightbox = null;
  let pointerGesture = null;
  let lastGesture = null;
  const observedMedia = new WeakSet();

  const getLightbox = () => {
    lightbox ||= new CTCustomallLightbox();
    return lightbox;
  };

  const followedCarouselDrag = (image) =>
    lastGesture && lastGesture.image === image && lastGesture.moved && performance.now() - lastGesture.time < 800;

  const openFromEvent = (event, image) => {
    if (event.button !== undefined && event.button !== 0) return;
    if (followedCarouselDrag(image)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    getLightbox().open(image);
  };

  const enhanceImage = (image) => {
    if (image.dataset.ctCustomallMediaReady === 'true') return;
    image.dataset.ctCustomallMediaReady = 'true';
    image.tabIndex = 0;
    image.setAttribute('role', 'button');
    image.setAttribute('aria-label', 'Open product image viewer');
    image.addEventListener('click', (event) => openFromEvent(event, image), true);
    image.addEventListener(
      'keydown',
      (event) => {
        if (!['Enter', ' '].includes(event.key)) return;
        openFromEvent(event, image);
      },
      true
    );
    image.addEventListener('contextmenu', (event) => event.stopImmediatePropagation(), true);
  };

  const observeMedia = (media) => {
    if (observedMedia.has(media)) return;
    observedMedia.add(media);

    const observer = new MutationObserver((mutations) => {
      media.querySelectorAll(MAIN_IMAGE_SELECTOR).forEach(enhanceImage);
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.target instanceof HTMLImageElement) {
          lightbox?.refreshCurrentImage(mutation.target);
        }
      });
    });

    observer.observe(media, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });
  };

  const initialize = (root = document) => {
    root.querySelectorAll?.(MAIN_IMAGE_SELECTOR).forEach(enhanceImage);
    root.querySelectorAll?.(MEDIA_SELECTOR).forEach(observeMedia);
  };

  window.addEventListener(
    'contextmenu',
    (event) => {
      if (!(event.target instanceof Element) || !event.target.closest(MAIN_IMAGE_SELECTOR)) return;
      event.stopImmediatePropagation();
    },
    true
  );

  window.addEventListener(
    'pointerdown',
    (event) => {
      if (event.button > 0 || !(event.target instanceof Element)) return;
      const image = event.target.closest(MAIN_IMAGE_SELECTOR);
      if (!image) return;
      pointerGesture = { image, pointerId: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
    },
    true
  );

  window.addEventListener(
    'pointermove',
    (event) => {
      if (!pointerGesture || event.pointerId !== pointerGesture.pointerId) return;
      if (Math.hypot(event.clientX - pointerGesture.x, event.clientY - pointerGesture.y) > DRAG_THRESHOLD) {
        pointerGesture.moved = true;
      }
    },
    true
  );

  window.addEventListener(
    'pointerup',
    (event) => {
      if (!pointerGesture || event.pointerId !== pointerGesture.pointerId) return;
      lastGesture = { image: pointerGesture.image, moved: pointerGesture.moved, time: performance.now() };
      pointerGesture = null;
    },
    true
  );

  window.addEventListener(
    'click',
    (event) => {
      if (event.button !== 0 || !(event.target instanceof Element)) return;
      const image = event.target.closest(MAIN_IMAGE_SELECTOR);
      if (!image) return;

      openFromEvent(event, image);
    },
    true
  );

  window.addEventListener(
    'keydown',
    (event) => {
      if (!['Enter', ' '].includes(event.key) || !(event.target instanceof Element)) return;
      const image = event.target.closest(MAIN_IMAGE_SELECTOR);
      if (!image) return;
      openFromEvent(event, image);
    },
    true
  );

  const documentObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) initialize(node.matches(MEDIA_SELECTOR) ? node.parentElement || node : node);
      });
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialize();
      documentObserver.observe(document.body, { childList: true, subtree: true });
    });
  } else {
    initialize();
    documentObserver.observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
